<template>
  <div v-if="localShow" :id="id + '___FB_modal_outer_'" class="tw-absolute tw-z-modal custom-modal">
    <ModalRack
      :id="id"
      ref="customModalRack"
      :is-static="true"
      :on-click-out="onClickOut"
      :is-hidden="isHidden"
      :size="size"
      :centered="centered"
      :dialog-class="dialogClass"
    >
      <div
        ref="customModalContent"
        tabindex="-1"
        class="tw-relative tw-flex tw-flex-col tw-w-full tw-rounded-20 tw-border tw-border-primary tw-bg-white tw-text-gray-600"
        :class="contentClass"
        data-label="modal-content"
        @keydown.esc="onEsc"
      >
        <header
          v-if="!hideHeader"
          class="tw-flex tw-items-start tw-justify-between tw-p-4 tw-border-b tw-border-gray-300 tw-rounded-tl-md"
          :class="headerClass"
          data-label="modal-header"
        >
          <slot name="modal-header">
            <h5 class="tw-leading-normal tw-mb-0">
              <slot name="modal-title" />
            </h5>
            <button
              aria-label="Close"
              class="
              tw-rounded-lg
              tw-inline-flex
              tw-focus:ring-gray-300
              tw-focus:ring-2
              tw-text-gray-400
              tw-hover:text-gray-900
              tw-hover:bg-gray-100
              tw-p-4
              tw--m-4
              tw-ml-auto
            "
              type="button"
              @click="onClose"
            >
              <span class="sr-only">
                Close
              </span>
              <XIcon size="1.35x" class="tw-text-gray-800 hover:tw-text-gray-500"/>
            </button>
          </slot>
        </header>
        <div
          class="tw-flex-auto tw-relative tw-rounded-lg tw-p-4"
          :class="bodyClass"
          data-label="modal-body"
        >
          <slot name="modal-body"></slot>
        </div>
        <div
          v-if="!hideFooter"
          class="tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-px-6 tw-pb-6"
          :class="[
            showFooterBorder ? 'tw-border-t tw-border-gray-300' : '',
            footerClass,
          ]"
          data-label="modal-footer"
        >
          <slot name="modal-footer">
            <button
              type="button"
              class="tw-min-w-[10rem] tw-border tw-border-gray-300/95 tx-weight-bold border-rounded-20 mn-h-46 tw-w-full md:tw-w-1/3 lg:tw-w-1/4 tx-tf-upper"
              :class="cancelButtonStyle"
              @click="handleCancel"
            >
              {{ cancelTitle }}
            </button>
            <button
              type="button"
              class="tw-min-w-[10rem] tw-border tw-border-gray-300/95 tx-weight-bold border-rounded-20 mn-h-46 tw-w-full md:tw-w-1/3 lg:tw-w-1/4 tx-tf-upper"
              :class="okButtonStyle"
              @click="handleOk"
            >
              {{ okTitle }}
            </button>
          </slot>
        </div>
      </div>
    </ModalRack>
    <FabricModalBackdrop v-if="!hideBackdrop" />
  </div>
</template>

<script>
  import { EVENT_MODAL_SHOW, EVENT_MODAL_HIDE } from '../plugins/Modal/constants'
  import ModalRack from './modal-rack.vue'
  import FabricModalBackdrop from './modal-backdrop.vue'
  import {
    XIcon,
  } from 'vue-feather-icons'

  export default {
    name: 'FabricModal',
    components: {
      XIcon,
      ModalRack,
      FabricModalBackdrop,
    },
    props: {
      id: {
        type: [String],
        default: '',
      },
      title: {
        type: [String],
        default: '',
      },
      okTitle: {
        type: [String],
        default: 'Ok',
      },
      cancelTitle: {
        type: [String],
        default: 'Cancel',
      },
      okVariant: {
        type: [String],
        default: 'primary',
      },
      cancelVariant: {
        type: [String],
        default: 'secondary',
      },
      size: {
        type: [String],
        default: 'xl',
      },
      hideHeader: {
        type: [Boolean],
        default: false
      },
      hideBody: {
        type: [Boolean],
        default: false
      },
      hideFooter: {
        type: [Boolean],
        default: false
      },
      dialogClass: {
        type: String,
        default: '',
      },
      modalClass: {
        type: [String],
        default: '',
      },
      contentClass: {
        type: [String],
        default: '',
      },
      headerClass: {
        type: [String],
        default: '',
      },
      bodyClass: {
        type: [String],
        default: '',
      },
      footerClass: {
        type: [String],
        default: '',
      },
      showFooterBorder: {
        type: [Boolean],
        default: false,
      },
      centered: {
        type: [Boolean],
        default: false,
      },
      noCloseOnEsc: {
        type: [Boolean],
        default: false,
      },
      noCloseOnBackdrop: {
        type: [Boolean],
        default: false,
      },
    },
    data() {
      return {
        EVENT_MODAL_SHOW,
        EVENT_MODAL_HIDE,
        localShow: false,
        isHidden: true,
        hideBackdrop: false,
        buttonStypeMapping: {
          primary: 'tw-text-gray-300 tw-bg-white',
          danger: 'tw-text-white tw-bg-orange-600',
          secondary: 'tw-text-gray-300 tw-bg-white',
        }
      }
    },
    computed: {
      okButtonStyle() {
        return this.buttonStypeMapping[this.okVariant] ?? ''
      },
      cancelButtonStyle() {
        return this.buttonStypeMapping[this.cancelVariant] ?? ''
      },
    },
    mounted() {
      this.$root.$on(EVENT_MODAL_SHOW, dataEvent => {
        const { id } = dataEvent
        if (id === this.id) {
          this.onOpen()
        } else {
          this.onClose()
        }
      })

      this.$root.$on(EVENT_MODAL_HIDE, dataEvent => {
        const { id } = dataEvent
        if (id === this.id) {
          this.onClose()
        }
      })
    },
    methods: {
      handleOk(event) {
        const handleCallback = status => status ? this.onClose() : null
        this.$emit('ok', event, handleCallback)
      },
      handleCancel() {
        this.onClose()
      },
      onOpen() {
        this.$emit('show')
        this.localShow = true
        this.hideBackdrop = false
        document.body.classList.add('tw-overflow-hidden')
        setTimeout(() => {
          this.isHidden = false
          if (this.$refs.customModalContent) {
            this.$refs.customModalContent.focus();
          }
        }, 300)
        this.$emit('shown', this.id)
      },
      onClose() {
        this.isHidden = true
        document.body.classList.remove('tw-overflow-hidden')
        setTimeout(() => {
          this.hideBackdrop = true
          this.localShow = false
        }, 400)
      },
      contains(parent, child) {
        return parent && typeof parent.contains === 'function'
          ? parent.contains(child)
          : false
      },
      onClickOut(event) {
        if (!document.body.contains(event.target)) {
          return
        }

        if (!this.contains(this.$refs.customModalContent, event.target) && !this.noCloseOnBackdrop) {
          this.onClose()
        }
      },
      onEsc(event) {
        if (!this.noCloseOnEsc && event.keyCode === 27 && this.localShow) {
          this.onClose()
        }
      },
    },
  }
</script>
